import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Template } from '../../types/templates';
import { Button } from '../UI/Button';

interface TemplateEditorProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template | null;
  onSave: (templateData: Partial<Template>) => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  isOpen,
  onClose,
  template,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description);
      setContent(template.content || '');
    } else {
      setName('');
      setDescription('');
      setContent('');
    }
    setErrors({});
  }, [template]);

  // Validação em tempo real para nomes reservados
  useEffect(() => {
    if (name.trim()) {
      const normalizedName = name.toLowerCase().replace(/\s+/g, '');
      if (normalizedName === 'default' || normalizedName === 'folder') {
        setErrors(prev => ({
          ...prev,
          name: 'Os nomes "default" e "folder" são reservados e não podem ser utilizados'
        }));
      } else if (errors.name && errors.name.includes('reservados')) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    }
  }, [name, errors.name]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!name.trim()) {
      newErrors.name = 'O nome é obrigatório';
    } else if (name.length > 200) {
      newErrors.name = 'O nome deve ter no máximo 200 caracteres';
    } else {
      // Verificar nomes reservados (case insensitive, sem espaços)
      const normalizedName = name.toLowerCase().replace(/\s+/g, '');
      if (normalizedName === 'default' || normalizedName === 'folder') {
        newErrors.name = 'Os nomes "default" e "folder" são reservados e não podem ser utilizados';
      }
    }

    if (!description.trim()) {
      newErrors.description = 'A descrição é obrigatória';
    } else if (description.length > 400) {
      newErrors.description = 'A descrição deve ter no máximo 400 caracteres';
    }

    if (!content.trim()) {
      newErrors.content = 'O conteúdo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const templateData: Partial<Template> = {
      name,
      description,
      content,
      isActive: true
    };

    if (template?.id) {
      templateData.id = template.id;
    }

    onSave(templateData);
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'bullet',
    'align',
    'indent',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video'
  ];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 mb-4"
                >
                  {template ? 'Editar Modelo' : 'Novo Modelo'}
                </Dialog.Title>

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Nome
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={200}
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors.name
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    />
                    <div className="flex justify-between items-center mt-1">
                    {errors.name && (
                        <p className="text-sm text-red-600">{errors.name}</p>
                    )}
                      <p className={`text-xs ${name.length > 180 ? 'text-orange-500' : 'text-gray-400'}`}>
                        {name.length}/200
                      </p>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Descrição
                    </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={400}
                      rows={3}
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        errors.description
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    />
                    <div className="flex justify-between items-center mt-1">
                    {errors.description && (
                        <p className="text-sm text-red-600">{errors.description}</p>
                    )}
                      <p className={`text-xs ${description.length > 350 ? 'text-orange-500' : 'text-gray-400'}`}>
                        {description.length}/400
                      </p>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="content"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Conteúdo
                    </label>
                    <div className="h-[600px]">
                      <ReactQuill
                        theme="snow"
                        value={content}
                        onChange={setContent}
                        modules={modules}
                        formats={formats}
                        className={`h-[500px] ${
                          errors.content ? 'border-red-300' : ''
                        }`}
                      />
                    </div>
                    {errors.content && (
                      <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleSave}
                  >
                    Salvar
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TemplateEditor; 